/**
 * @file MovementsSummary.tsx
 * @description Итог по отбору над журналом движений: «Показано / Приход /
 * Расход / Сальдо» (phase19.31, SC69 — замечание владельца «плашки нужно
 * сделать более заметными, там важная информация»).
 *
 * Общий `MetricTile` в компактном виде: белая непрозрачная плитка ~76px,
 * подпись вторым цветом, значение 20px/600. «Приход» — зелёным с «+»,
 * «Расход» — янтарным с «−» (обычная операция, не ошибка), «Сальдо» —
 * основным цветом со знаком. Раскладка: телефон и планшет — 2×2, от 1024 —
 * четыре в ряд.
 */

import { MetricTile, MetricTileSkeleton, type MetricValueTone } from "../../ui/MetricTile";
import { formatSignedQty, type MovementsSummary as Summary } from "../../../lib/movements-summary";

interface Props {
  /** Строк после клиентского отбора. */
  shown: number;
  /** Строк в окне журнала с сервера. */
  total: number;
  summary: Summary;
  /** Единица — только когда в отборе одна фасовка (« шт»), иначе пусто. */
  unit: string;
}

/** Раскладка плиток — общая у итога и заготовки, чтобы высота не прыгала. */
const SUMMARY_GRID_CLASS = "grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3";

/**
 * Заготовка итога на время загрузки журнала: четыре плитки той же высоты.
 * Раньше блок итога появлялся только после ответа сервера — на снимке,
 * сделанном во время загрузки, плиток «не было» (приёмка qa4, п. 15), а
 * страница прыгала на 76px вниз, когда они появлялись.
 */
export function MovementsSummarySkeleton() {
  return (
    <div aria-hidden className={SUMMARY_GRID_CLASS}>
      {["shown", "income", "outcome", "balance"].map((key) => (
        <MetricTileSkeleton key={key} size="compact" />
      ))}
    </div>
  );
}

interface Tile {
  key: string;
  label: string;
  value: string;
  valueTone: MetricValueTone;
}

export function MovementsSummary({ shown, total, summary, unit }: Props) {
  const tiles: Tile[] = [
    { key: "shown", label: "Показано", value: `${shown} из ${total}`, valueTone: "default" },
    {
      key: "income",
      label: "Приход",
      value: `${formatSignedQty(summary.income)}${unit}`,
      valueTone: summary.income > 0 ? "success" : "default",
    },
    {
      key: "outcome",
      label: "Расход",
      value: `${formatSignedQty(summary.outcome)}${unit}`,
      valueTone: summary.outcome < 0 ? "warning" : "default",
    },
    { key: "balance", label: "Сальдо", value: `${formatSignedQty(summary.balance)}${unit}`, valueTone: "default" },
  ];

  return (
    <section aria-label="Итог по отбору" className={SUMMARY_GRID_CLASS}>
      {tiles.map((tile) => (
        <MetricTile key={tile.key} size="compact" label={tile.label} value={tile.value} valueTone={tile.valueTone} />
      ))}
    </section>
  );
}
